import { useMemo, useState } from 'react';

import { GridApi } from '@mint-lib/ag-grid/community';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  itemVariants,
  Label,
  TruncateText,
} from '@mint-lib/tw-components';
import { MediaPlan } from '@mint-sdk/api-media-plan';

import { useColumnsApi } from '../../../../../../api/queries/useColumnsApi.js';

type AddMediaItemsProps = {
  mediaData: MediaPlan.MediaData;
  childOptions: MediaPlan.MediaItemChildOptions;
  onClose: () => void;
  api: GridApi<MediaPlan.MediaData>;
};

const AddMediaItems = ({
  childOptions,
  onClose,
  mediaData,
  api,
}: AddMediaItemsProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: columnsData } = useColumnsApi();

  const { mutateAsync: addMediaItemChildren } =
    MediaPlan.useAddMediaItemChildren();

  const handleAddRows = async () => {
    onClose();

    const context = api.getGridOption('context');

    api.setGridOption('context', {
      ...context,
      rowIdsToExpand: [...context.rowIdsToExpand, mediaData.id],
    });

    try {
      await addMediaItemChildren({
        urlParams: {
          mediaItemId: mediaData.mediaItemId,
        },
        data: {
          levelEntitiesIds: Array.from(selected),
        },
      });
    } catch {
      enqueueSnackbar({
        title: t('Failed to add rows'),
        variant: 'error',
        removable: true,
      });
    }
  };

  const handleOptionSelect = (value: string) => {
    setSelected((selected) => {
      const newSelected = new Set(selected);

      if (newSelected.has(value)) {
        newSelected.delete(value);
      } else {
        newSelected.add(value);
      }

      return newSelected;
    });
  };

  const levelLabel = useMemo(() => {
    const column = columnsData?.items.find(
      (column) => column.id === childOptions.levelUrn,
    );

    return column?.label ?? childOptions.levelType;
  }, [childOptions, columnsData]);

  return (
    <div className="w-74 px-3 py-1">
      <div className="mb-3 flex items-center justify-between">
        <h6 className="mb-0 text-sm text-text-secondary">
          {t('Add {{levelLabel}} inside', { levelLabel })}
        </h6>
        <Button
          className="w-[43px]"
          color="secondary"
          variant="contained"
          size="small"
          onClick={handleAddRows}
          disabled={selected.size === 0}
          aria-label="Add rows submit"
          data-dd-action-name="Add media items: add"
        >
          {t('Add')}
        </Button>
      </div>
      <div>
        <p className="m-0 mb-1 text-xs text-text-secondary first-letter:uppercase">
          {levelLabel}
        </p>
        <Command>
          <CommandInput placeholder={t('Search')} />
          <CommandList>
            <CommandEmpty>
              {t('No {{levelLabel}} found.', { levelLabel })}
            </CommandEmpty>
            <CommandGroup>
              {childOptions.available.map((option) => (
                <CommandItem
                  key={option.value}
                  data-dd-action-name="Add media items: toggle item"
                  className={itemVariants({ selectionListItem: 'default' })}
                  onSelect={() => {
                    handleOptionSelect(option.value);
                  }}
                  value={option.label}
                >
                  <Checkbox
                    id={option.value}
                    checked={selected.has(option.value)}
                  />

                  <TruncateText>
                    <Label
                      htmlFor={option.value}
                      onClick={(e) => e.stopPropagation()} // prevent click event to be fired twice
                      className="block"
                    >
                      {option.label}
                    </Label>
                  </TruncateText>
                </CommandItem>
              ))}
            </CommandGroup>
            {childOptions.added.length > 0 && (
              <CommandGroup heading={t('Already added')}>
                {childOptions.added.map((option) => (
                  <CommandItem
                    key={option.value}
                    className={itemVariants({
                      selectionListItem: 'selected',
                    })}
                    value={option.label}
                  >
                    <Checkbox id={option.value} checked />
                    <Label htmlFor={option.value}>{option.label}</Label>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </div>
    </div>
  );
};

export default AddMediaItems;
